// FIRST, before Sentry/PostHog/Clarity are imported or initialised: strip the
// onboarding magic-link token out of the URL so it is never recorded as
// $current_url, in a session replay, or in a breadcrumb (issue #3457 §7).
import "./utils/portalAccessToken";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import store from "./store";

const isProd = process.env.REACT_APP_SERVER_TYPE === "prod";
const postHogKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY as string;

// GOD mode (admin login-as-user): the early inline script sets this flag on the
// /impersonate handoff page and on any tab holding an imp_ token, so trackers
// never initialize during impersonation.
const inGodMode = (): boolean =>
  typeof window !== "undefined" && (window as { __NUDGE_GOD_MODE__?: boolean }).__NUDGE_GOD_MODE__ === true;

// Bearer tokens (DRF `Token <key>` and impersonation `imp_…` keys) ride in
// AxiosError.config.headers.Authorization, which captureConsoleIntegration can
// export to Sentry. Redact them from every outgoing event.
const TOKEN_PATTERN = /\b(?:Token\s+|imp_)[A-Za-z0-9]+/g;
const redactSecrets = (value: unknown, depth = 0): unknown => {
  if (value == null || depth > 6) return value;
  if (typeof value === "string") return value.replace(TOKEN_PATTERN, "[redacted]");
  if (Array.isArray(value)) return value.map((v) => redactSecrets(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = /authorization/i.test(k) ? "[redacted]" : redactSecrets(v, depth + 1);
    }
    return out;
  }
  return value;
};

if (isProd && !inGodMode()) {
  posthog.init(postHogKey, {
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
    // Disable session recordings cos we're already using Clarity and Sentry
    // Reference: https://posthog.com/docs/session-replay/how-to-control-which-sessions-you-record
    disable_session_recording: true,
    // Autocapture is already disabled via PostHog dashboard settings
    before_send: (event) => {
      if (!event || !process.env.REACT_APP_VERSION) {
        return event;
      }
      event.properties = {
        ...event.properties,
        app_version: process.env.REACT_APP_VERSION,
      };
      return event;
    },
  });
}

Sentry.init({
  dsn: "https://068f74177d2925c91249ba816ca09ab6@o4506998898688000.ingest.us.sentry.io/4507005146759168",
  environment: process.env.REACT_APP_SERVER_TYPE,
  release: process.env.REACT_APP_VERSION,
  integrations: [
    Sentry.browserTracingIntegration(),
    // error only: console.warn still lands as a breadcrumb on real events, but
    // no longer creates standalone issues — warn-level noise (library
    // deprecations, per-chunk degraded-path logs) was burying actionable
    // errors, and every intentional warn-path already has a bounded
    // Sentry.capture* or product event of its own.
    Sentry.captureConsoleIntegration({
      levels: ["error"],
    }),
  ],
  beforeSend: (event) => {
    try {
      return redactSecrets(event) as typeof event;
    } catch {
      return event;
    }
  },
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^\//],
  ignoreErrors: [
    // Axios request cancellations (e.g. when the user switches clients/pages mid-fetch)
    // are intentional aborts, not bugs. They were the top noise source in Sentry.
    "CanceledError",
    "AbortError",
    "Request aborted",
    "canceled",
    "google.accounts.id.initialize() is called multiple times",
  ],
});

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <PostHogProvider client={posthog}>
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
  </PostHogProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//
reportWebVitals();
